/**
 * MODEL / CONFIDENCE BADGE CONFIG
 *
 * Task 18 (Hermès Repolish Sweep): re-tokenized to the design-system.md badge
 * spec — ONE color per provider, expressed as CSS custom-property references
 * so both themes resolve correctly (dark keeps the exact md-spec values;
 * light mode gets the AA-darkened flips for free):
 *   Claude → --text-orange   (= --solomon-orange in dark; #E8441A in light)
 *   GPT    → --status-success
 *   Gemini → --status-info
 * The three Claude sub-model shades (#E07B39/#C96A28/#B85C22) are RETIRED:
 * they were near-indistinguishable at 11px, all off-token (nearest the retired
 * #E8621A family), and the badge's icon+label (◆ Opus / ◇ Sonnet / ◈ Haiku)
 * already carries the sub-model identity. Operator rules at the T18 gate.
 *
 * `variant` maps each entry onto the shared .sol-badge--* primitive classes
 * (primitives.css); `color` remains for JS-side consumers (regen dropdown
 * icons) as a var() reference — zero raw hex in this file.
 *
 * 2026-08-07: the hardcoded MODEL_CONFIG ID map is GONE. It had rotted to
 * gpt-4o / claude-opus-4-6 / gemini-1.5-2.x while the backend ran gpt-5.6-sol /
 * claude-opus-4-8 / gemini-3.5-flash, and survived only on its partial-match
 * fallbacks. Provider identity now resolves by family in src/lib/modelRegistry.js,
 * shared with ConversationSidebar and ActionCard. Do not reintroduce an ID map.
 */

import { resolveModel, ERROR_DESCRIPTOR, SYSTEM_DESCRIPTOR } from '../../lib/modelRegistry.js';

// ============================================================
// MODEL CONFIG (Day 5 — registry-backed since 2026-08-07)
// ============================================================

export function getModelConfig(model) {
  if (!model) return null;
  if (model === 'error') return ERROR_DESCRIPTOR;
  // The ask-and-wait turn: Solomon spoke, no provider did (routing V3 rev 3.2).
  if (model === 'system') return SYSTEM_DESCRIPTOR;
  // Unrecognised ID: show the raw string rather than inventing a provider.
  // No logo on the raw fallback: an unrecognised id renders the glyph + the formatted id.
  return resolveModel(model)
    ?? { label: model, icon: '◆', variant: 'claude', color: 'var(--text-orange)' };
}

export { formatModelName } from '../../lib/modelRegistry.js';

// ============================================================
// CONFIDENCE FLAG CONFIG (Day 5)
// ============================================================

export const FLAG_CONFIG = {
  yellow: { symbol: '⚠', label: 'Uncertain routing', color: 'var(--status-warning)', title: 'Solomon is less certain which model fits best here' },
  red:    { symbol: '🔴', label: 'Low confidence',   color: 'var(--status-error)',   title: 'Low routing confidence — consider using a manual override' },
};

// ============================================================
// MODE BADGE + SWITCH DROPDOWN — regenerate-with-model options
// ============================================================

export const REGENERATE_MODELS = [
  { id: 'claude',  label: 'Claude',  icon: '◆', color: 'var(--text-orange)',     description: 'Deep reasoning' },
  { id: 'gpt',     label: 'GPT',     icon: '⬡', color: 'var(--status-success)',  description: 'Creative writing' },
  { id: 'gemini',  label: 'Gemini',  icon: '✦', color: 'var(--status-info)',     description: 'Fast & factual' },
];
